import { createListenerMiddleware } from '@reduxjs/toolkit';
import { monitoring } from 'common/services';
import { MediaSettingsTab } from 'devices/types';
import type { RootState } from 'store';
import { selectNoCameraFound, setMediaSettingsIsOpen } from 'user/state';

import { mediaSettingsActions } from './mediaSettings.actions';

export const mediaSettingsListener = createListenerMiddleware<RootState>();

// Track when microphone is in use by another app
mediaSettingsListener.startListening({
  actionCreator: mediaSettingsActions.microphoneTrack.creation.failed,
  effect: ({ payload: errorKey }) => {
    if (errorKey === 'deviceAlreadyInUse') {
      monitoring.trackAction('devices.microphone.in_use');
    }
  },
});

// Track when camera is in use by another app
mediaSettingsListener.startListening({
  actionCreator: mediaSettingsActions.cameraTrack.creation.failed,
  effect: ({ payload: errorKey }) => {
    if (errorKey === 'deviceAlreadyInUse') {
      monitoring.trackAction('devices.camera.in_use');
    }
  },
});

// Track microphone in_use recovery (user closed other app and retried successfully)
mediaSettingsListener.startListening({
  actionCreator: mediaSettingsActions.microphoneTrack.creation.started,
  effect: async (_, { getOriginalState, take }) => {
    const { microphoneTrack } = getOriginalState().mediaSettings;
    if (microphoneTrack.error !== 'deviceAlreadyInUse') return;

    const [action] = await take(
      (action) =>
        mediaSettingsActions.microphoneTrack.creation.completed.match(action) ||
        mediaSettingsActions.microphoneTrack.creation.failed.match(action)
    );

    if (mediaSettingsActions.microphoneTrack.creation.completed.match(action)) {
      monitoring.trackAction('devices.microphone.in_use_recovered');
    }
  },
});

// Track camera in_use recovery (user closed other app and retried successfully)
mediaSettingsListener.startListening({
  actionCreator: mediaSettingsActions.cameraTrack.creation.started,
  effect: async (_, { getOriginalState, take }) => {
    const { cameraTrack } = getOriginalState().mediaSettings;
    if (cameraTrack.error !== 'deviceAlreadyInUse') return;

    const [action] = await take(
      (action) =>
        mediaSettingsActions.cameraTrack.creation.completed.match(action) ||
        mediaSettingsActions.cameraTrack.creation.failed.match(action)
    );

    if (mediaSettingsActions.cameraTrack.creation.completed.match(action)) {
      monitoring.trackAction('devices.camera.in_use_recovered');
    }
  },
});

mediaSettingsListener.startListening({
  actionCreator: mediaSettingsActions.noDevicesAlert.openMediaSettingsButtonClicked,
  effect: (_, { dispatch, getState }) => {
    const noCameraFound = selectNoCameraFound(getState());
    dispatch(
      setMediaSettingsIsOpen({
        open: true,
        initialTab: !noCameraFound ? MediaSettingsTab.Audio : MediaSettingsTab.Video,
      })
    );
  },
});
